import { apiClient } from './client';
import { PaginatedResponse } from '../types/pagination';
import {
  ALStream,
  ApprovalStatus,
  AssignmentHistoryEntry,
  AssignmentType,
  DuplicatePersonCheckResult,
  FollowUp,
  FollowUpType,
  FundingSource,
  Gender,
  LeadAttachment,
  LeadConversionReadiness,
  LeadDetail,
  LeadListItem,
  LeadReferredFriend,
  OlAlType,
  SubjectResult,
  TimelineEntry,
} from '../types/lead';

export interface ListLeadsParams {
  page: number;
  pageSize: number;
  search?: string;
  leadStatusId?: number;
  leadSourceId?: number;
  assignedToId?: number;
  branchId?: number;
  countryId?: number;
  assignmentType?: AssignmentType;
  approvalStatus?: ApprovalStatus;
  dateFrom?: string;
  dateTo?: string;
  unassigned?: boolean;
  /** "Pending Leads by Counselor" click-through (Counselor Dashboard §5) —
   * still requires Counselor action: not terminal, approved, not yet
   * converted to a Student. */
  pending?: boolean;
}

export const listLeads = async (params: ListLeadsParams): Promise<PaginatedResponse<LeadListItem>> => {
  const { data } = await apiClient.get('/leads', { params });
  return data;
};

export const getLead = async (id: number): Promise<LeadDetail> => {
  const { data } = await apiClient.get(`/leads/${id}`);
  return data.data;
};

export interface CreateLeadPayload {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  countryId?: number;
  /** Required by the backend on real creation, but optional here since the wizard's autosave
   * payload-builder is shared with PATCH calls where it may legitimately be omitted. */
  leadSourceId?: number;
  branchId?: number;
  interestedCourseId?: number;
  /** Creates the lead in "Draft" status instead of "New" — used by the wizard's Step 1 autosave. */
  saveAsDraft?: boolean;
  /** Central Lead Desk (Phase 20): only honored when the caller holds leads:assign. */
  assignedToId?: number;

  gender?: Gender;
  dateOfBirth?: string;
  nicOrPassport?: string;
  nationalityCountryId?: number;
  address?: string;
  city?: string;
  postalCode?: string;
  telephone?: string;

  schoolName?: string;
  olType?: OlAlType;
  olYear?: number;
  olResults?: SubjectResult[];
  alType?: OlAlType;
  alYear?: number;
  alStream?: ALStream;
  alResults?: SubjectResult[];
  overallGpa?: number;

  preferredQualificationLevelId?: number;
  highestQualificationLevelId?: number;
  preferredStudyAreaId?: number;
  preferredUniversityId?: number;
  preferredIntakeId?: number;
  preferredIntakeLabel?: string;
  budget?: number;
  budgetCurrencyId?: number;

  fatherName?: string;
  fatherPhone?: string;
  fatherOccupation?: string;
  fatherOrganization?: string;
  fatherOfficePhone?: string;
  fatherEmail?: string;
  motherName?: string;
  motherPhone?: string;
  motherOccupation?: string;
  motherOrganization?: string;
  motherOfficePhone?: string;
  motherEmail?: string;
  parentsMet?: boolean;
  guardianName?: string;
  guardianRelationship?: string;
  guardianPhone?: string;
  guardianEmail?: string;

  expectedInvestment?: number;
  incomeRange?: string;
  monthlyIncome?: number;
  scholarshipRequired?: boolean;
  fundingSource?: FundingSource;
  sponsorDetails?: string;

  campaignId?: number;
  referralTypeId?: number;
  referrerName?: string;
  referrerContact?: string;
  /** The specific advertisement/campaign identifier, distinct from
   * leadSourceId (the channel) — manual entry. */
  adIdName?: string;
  notes?: string;

  lastCompletedStep?: number;
}

export const createLead = async (payload: CreateLeadPayload): Promise<LeadDetail> => {
  const { data } = await apiClient.post('/leads', payload);
  return data.data;
};

export const updateLead = async (id: number, payload: Partial<CreateLeadPayload>): Promise<LeadDetail> => {
  const { data } = await apiClient.patch(`/leads/${id}`, payload);
  return data.data;
};

export const deleteLead = async (id: number): Promise<void> => {
  await apiClient.delete(`/leads/${id}`);
};

export const changeLeadStatus = async (id: number, leadStatusId: number): Promise<LeadDetail> => {
  const { data } = await apiClient.post(`/leads/${id}/status`, { leadStatusId });
  return data.data;
};

export const assignLead = async (id: number, userId: number): Promise<LeadDetail> => {
  const { data } = await apiClient.post(`/leads/${id}/assign`, { userId });
  return data.data;
};

export const transferLead = async (id: number, toUserId: number, reason?: string): Promise<LeadDetail> => {
  const { data } = await apiClient.post(`/leads/${id}/transfer`, { toUserId, reason });
  return data.data;
};

export const bulkAssignLeads = async (
  leadIds: number[],
  userId: number,
  reason?: string,
): Promise<{ updated: number }> => {
  const { data } = await apiClient.post('/leads/bulk-assign', { leadIds, userId, reason });
  return data.data;
};

export const bulkTransferLeads = async (
  leadIds: number[],
  toBranchId: number,
  reason?: string,
): Promise<{ updated: number }> => {
  const { data } = await apiClient.post('/leads/bulk-transfer', { leadIds, toBranchId, reason });
  return data.data;
};

export const bulkChangeLeadStatus = async (
  leadIds: number[],
  leadStatusId: number,
): Promise<{ updated: number }> => {
  const { data } = await apiClient.post('/leads/bulk-status', { leadIds, leadStatusId });
  return data.data;
};

export const listReferredFriends = async (leadId: number): Promise<LeadReferredFriend[]> => {
  const { data } = await apiClient.get(`/leads/${leadId}/referred-friends`);
  return data.data;
};

export const addReferredFriend = async (
  leadId: number,
  payload: { name: string; phone?: string; sortOrder?: number },
): Promise<LeadReferredFriend> => {
  const { data } = await apiClient.post(`/leads/${leadId}/referred-friends`, payload);
  return data.data;
};

export const deleteReferredFriend = async (friendId: number): Promise<void> => {
  await apiClient.delete(`/leads/referred-friends/${friendId}`);
};

export const listFollowUps = async (leadId: number): Promise<FollowUp[]> => {
  const { data } = await apiClient.get(`/leads/${leadId}/follow-ups`);
  return data.data;
};

export const addFollowUp = async (
  leadId: number,
  payload: { type: FollowUpType; summary: string; outcome?: string; nextFollowUpAt?: string },
): Promise<FollowUp> => {
  const { data } = await apiClient.post(`/leads/${leadId}/follow-ups`, payload);
  return data.data;
};

export const getLeadTimeline = async (leadId: number): Promise<TimelineEntry[]> => {
  const { data } = await apiClient.get(`/leads/${leadId}/timeline`);
  return data.data;
};

/** Convert-to-Student workflow correction: readiness summary (Personal
 * Appointment / Application / Registration Fee / permission) backing the
 * Lead detail page's "NEXT STEPS" panel and the Convert to Student gate —
 * the same conditions POST /students/:id/finalize enforces server-side. */
export const getLeadConversionReadiness = async (leadId: number): Promise<LeadConversionReadiness> => {
  const { data } = await apiClient.get(`/leads/${leadId}/conversion-readiness`);
  return data.data;
};

export const listAttachments = async (leadId: number): Promise<LeadAttachment[]> => {
  const { data } = await apiClient.get(`/leads/${leadId}/attachments`);
  return data.data;
};

export const uploadAttachment = async (
  leadId: number,
  file: File,
  documentTypeId?: number,
): Promise<LeadAttachment> => {
  const formData = new FormData();
  formData.append('file', file);
  if (documentTypeId) formData.append('documentTypeId', String(documentTypeId));
  const { data } = await apiClient.post(`/leads/${leadId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.data;
};

export const deleteAttachment = async (attachmentId: number): Promise<void> => {
  await apiClient.delete(`/leads/attachments/${attachmentId}`);
};

export const downloadAttachment = async (
  attachmentId: number,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await apiClient.get(`/leads/attachments/${attachmentId}/download`, {
    responseType: 'blob',
  });
  const disposition = (response.headers['content-disposition'] as string | undefined) ?? '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: response.data, filename: match?.[1] ?? `attachment-${attachmentId}` };
};

export const importLeadsCsv = async (
  file: File,
): Promise<{
  totalRows: number;
  created: number;
  failed: number;
  errors: Array<{ row: number; message: string }>;
  skippedDuplicates: number;
  duplicates: Array<{ row: number; message: string }>;
}> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post('/leads/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.data;
};

export const exportLeadsCsv = async (filters: Partial<ListLeadsParams>): Promise<Blob> => {
  const { data } = await apiClient.get('/leads/export', { params: filters, responseType: 'blob' });
  return data;
};

export interface ListAssignmentHistoryParams {
  page: number;
  pageSize: number;
  leadId?: number;
  counselorId?: number;
  dateFrom?: string;
  dateTo?: string;
}

export const listAssignmentHistory = async (
  params: ListAssignmentHistoryParams,
): Promise<PaginatedResponse<AssignmentHistoryEntry>> => {
  const { data } = await apiClient.get('/leads/assignment-history', { params });
  return data;
};

export interface EligibleCounselor {
  id: number;
  firstName: string;
  lastName: string;
}

/** Backs every assign/transfer dialog's "Counselor" dropdown — a
 * leads:assign-gated endpoint, not a users:view/roles:view lookup, so
 * roles like Lead Manager (which can assign leads but holds neither of
 * those permissions) can still populate it. */
export const listEligibleCounselors = async (): Promise<EligibleCounselor[]> => {
  const { data } = await apiClient.get('/leads/eligible-counselors');
  return data.data;
};

/** Advisory duplicate-person check (searches both Leads and registered
 * Students) — the live/on-blur check and the "Check Existing Person"
 * button. The backend re-checks mandatorily before an actual create;
 * this call never blocks anything by itself. */
export const checkDuplicateLead = async (phone: string): Promise<DuplicatePersonCheckResult> => {
  const { data } = await apiClient.get('/leads/check-duplicate', { params: { phone } });
  return data.data;
};

export const approveLead = async (id: number): Promise<LeadDetail> => {
  const { data } = await apiClient.post(`/leads/${id}/approve`);
  return data.data;
};

export const rejectLead = async (id: number, reason: string): Promise<LeadDetail> => {
  const { data } = await apiClient.post(`/leads/${id}/reject`, { reason });
  return data.data;
};

/** Lead Manager's "leaving Counselor" workflow — transfer some or all of
 * one Counselor's leads to another in one operation. Omit leadIds to
 * transfer everything currently assigned to fromCounselorId. */
export const transferCounselorLeads = async (params: {
  fromCounselorId: number;
  toCounselorId: number;
  leadIds?: number[];
  reason?: string;
}): Promise<{ updated: number }> => {
  const { data } = await apiClient.post('/leads/transfer-counselor-leads', params);
  return data.data;
};
